import { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY_STORAGE = "honor_admin_key_v1";

const STATUS_LABELS = {
  nuevo: "Nuevo",
  contactado: "Contactado",
  confirmado: "Confirmado",
  asistio: "Asistió",
  no_asistio: "No asistió",
  descartado: "Descartado",
};

const STATUS_COLORS = {
  nuevo: "bg-[#BEDD7D]/15 text-[#BEDD7D] border-[#BEDD7D]/40",
  contactado: "bg-blue-400/15 text-blue-300 border-blue-400/40",
  confirmado: "bg-emerald-400/15 text-emerald-300 border-emerald-400/40",
  asistio: "bg-purple-400/15 text-purple-300 border-purple-400/40",
  no_asistio: "bg-orange-400/15 text-orange-300 border-orange-400/40",
  descartado: "bg-red-400/15 text-red-300 border-red-400/40",
};

export default function AdminLeads() {
  const [adminKey, setAdminKey] = useState(() => localStorage.getItem(KEY_STORAGE) || "");
  const [authed, setAuthed] = useState(false);
  const [keyInput, setKeyInput] = useState("");
  const [loginError, setLoginError] = useState("");
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_status: {} });
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("todos");

  const headers = useMemo(() => ({ "X-Admin-Key": adminKey }), [adminKey]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [leadsRes, statsRes] = await Promise.all([
        axios.get(`${API}/leads/workshop`, { headers }),
        axios.get(`${API}/leads/workshop/stats`, { headers }),
      ]);
      setLeads(leadsRes.data || []);
      setStats(statsRes.data || { total: 0, by_status: {} });
      setAuthed(true);
    } catch (err) {
      if (err?.response?.status === 401) {
        setAuthed(false);
        localStorage.removeItem(KEY_STORAGE);
        setAdminKey("");
      }
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    if (adminKey) fetchAll();
  }, [adminKey, fetchAll]);

  const handleLogin = (e) => {
    e.preventDefault();
    setLoginError("");
    const trimmed = keyInput.trim();
    if (!trimmed) {
      setLoginError("Ingresa la clave admin");
      return;
    }
    localStorage.setItem(KEY_STORAGE, trimmed);
    setAdminKey(trimmed);
  };

  const handleLogout = () => {
    localStorage.removeItem(KEY_STORAGE);
    setAdminKey("");
    setAuthed(false);
    setLeads([]);
    setKeyInput("");
  };

  const updateStatus = async (id, status) => {
    try {
      const { data } = await axios.patch(
        `${API}/leads/workshop/${id}`,
        { status },
        { headers }
      );
      setLeads((prev) => prev.map((l) => (l.id === id ? data : l)));
      // refresh stats
      const statsRes = await axios.get(`${API}/leads/workshop/stats`, { headers });
      setStats(statsRes.data);
    } catch (err) {
      alert("No se pudo actualizar el estado");
    }
  };

  const deleteLead = async (id) => {
    if (!window.confirm("¿Eliminar este lead?")) return;
    try {
      await axios.delete(`${API}/leads/workshop/${id}`, { headers });
      setLeads((prev) => prev.filter((l) => l.id !== id));
      const statsRes = await axios.get(`${API}/leads/workshop/stats`, { headers });
      setStats(statsRes.data);
    } catch (err) {
      alert("No se pudo eliminar");
    }
  };

  const downloadCSV = async () => {
    try {
      const res = await axios.get(`${API}/leads/workshop/export.csv`, {
        headers,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      const today = new Date().toISOString().slice(0, 10);
      link.setAttribute("download", `honor-leads-${today}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      alert("No se pudo descargar el CSV");
    }
  };

  const filtered = useMemo(() => {
    const s = search.toLowerCase().trim();
    return leads.filter((l) => {
      if (filterStatus !== "todos" && (l.status || "nuevo") !== filterStatus) return false;
      if (!s) return true;
      return (
        (l.name || "").toLowerCase().includes(s) ||
        (l.email || "").toLowerCase().includes(s) ||
        (l.whatsapp || "").toLowerCase().includes(s) ||
        (l.country || "").toLowerCase().includes(s)
      );
    });
  }, [leads, search, filterStatus]);

  if (!authed) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-4">
        <div className="w-full max-w-md rounded-2xl border border-[#3E4D36]/70 bg-[#0A0F08] p-8">
          <span className="text-[10px] font-display uppercase tracking-[0.4em] text-[#BEDD7D]">
            / Honor Admin
          </span>
          <h1 className="mt-3 font-display font-bold text-3xl uppercase tracking-tight text-white">
            Acceso <span className="italic text-[#BEDD7D]">restringido</span>
          </h1>
          <p className="mt-2 text-sm text-white/55 font-body">
            Ingresa tu clave de administrador para ver el panel de leads.
          </p>
          <form onSubmit={handleLogin} className="mt-6 space-y-3">
            <input
              type="password"
              value={keyInput}
              onChange={(e) => setKeyInput(e.target.value)}
              placeholder="Admin key"
              data-testid="admin-key-input"
              autoComplete="current-password"
              className="w-full bg-black/60 border border-[#3E4D36]/60 focus:border-[#BEDD7D] outline-none rounded-lg px-3 py-3 text-sm text-white font-body placeholder:text-white/30 transition-colors"
            />
            {loginError && (
              <p className="text-xs text-red-400 font-body">{loginError}</p>
            )}
            <button
              type="submit"
              data-testid="admin-login-btn"
              className="w-full rounded-lg bg-[#BEDD7D] hover:bg-white text-black font-display font-bold uppercase tracking-[0.22em] text-xs py-3.5 transition-all"
            >
              Entrar al panel
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid="admin-leads-page">
      {/* Header */}
      <header className="sticky top-0 z-30 backdrop-blur-md bg-black/85 border-b border-[#1f2a18]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div>
            <span className="text-[10px] font-display uppercase tracking-[0.4em] text-[#BEDD7D]">
              / Honor Admin
            </span>
            <h1 className="mt-1 font-display font-bold text-xl md:text-2xl uppercase tracking-tight">
              Panel de leads · Workshop
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={fetchAll}
              disabled={loading}
              data-testid="refresh-leads"
              className="px-3 py-2 rounded-lg border border-[#3E4D36] hover:border-[#BEDD7D] hover:text-[#BEDD7D] text-white/80 text-[10px] font-display font-bold uppercase tracking-[0.2em] transition-all disabled:opacity-50"
            >
              {loading ? "..." : "Refrescar"}
            </button>
            <button
              onClick={downloadCSV}
              data-testid="download-csv"
              className="px-3 py-2 rounded-lg bg-[#BEDD7D] hover:bg-white text-black text-[10px] font-display font-bold uppercase tracking-[0.2em] transition-all"
            >
              Exportar CSV
            </button>
            <button
              onClick={handleLogout}
              data-testid="admin-logout"
              className="px-3 py-2 rounded-lg border border-red-500/40 hover:border-red-400 text-red-300 hover:text-red-200 text-[10px] font-display font-bold uppercase tracking-[0.2em] transition-all"
            >
              Salir
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
          <StatCard label="Total" value={stats.total} highlight />
          {Object.keys(STATUS_LABELS).map((s) => (
            <StatCard
              key={s}
              label={STATUS_LABELS[s]}
              value={stats.by_status?.[s] ?? 0}
              status={s}
              onClick={() => setFilterStatus(filterStatus === s ? "todos" : s)}
              active={filterStatus === s}
            />
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nombre, email, WhatsApp o país..."
            data-testid="search-leads"
            className="flex-1 bg-[#0A0F08] border border-[#3E4D36]/60 focus:border-[#BEDD7D] outline-none rounded-lg px-3 py-2.5 text-sm text-white font-body placeholder:text-white/30 transition-colors"
          />
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            data-testid="filter-status"
            className="bg-[#0A0F08] border border-[#3E4D36]/60 focus:border-[#BEDD7D] outline-none rounded-lg px-3 py-2.5 text-sm text-white font-body transition-colors"
          >
            <option value="todos">Todos los estados</option>
            {Object.keys(STATUS_LABELS).map((s) => (
              <option key={s} value={s}>{STATUS_LABELS[s]}</option>
            ))}
          </select>
        </div>

        {/* Table */}
        <div className="rounded-xl border border-[#3E4D36]/50 overflow-hidden bg-[#0A0F08]">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="leads-table">
              <thead className="bg-[#0F1A0C] text-[10px] font-display uppercase tracking-[0.18em] text-white/60">
                <tr>
                  <th className="px-4 py-3 text-left">Fecha</th>
                  <th className="px-4 py-3 text-left">Nombre</th>
                  <th className="px-4 py-3 text-left">Email</th>
                  <th className="px-4 py-3 text-left">WhatsApp</th>
                  <th className="px-4 py-3 text-left">País</th>
                  <th className="px-4 py-3 text-left">Exp.</th>
                  <th className="px-4 py-3 text-left">Estado</th>
                  <th className="px-4 py-3 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-12 text-center text-white/40 font-body text-sm">
                      {loading ? "Cargando..." : "No hay leads que coincidan."}
                    </td>
                  </tr>
                )}
                {filtered.map((l) => {
                  const status = l.status || "nuevo";
                  const date = l.created_at ? new Date(l.created_at) : null;
                  const wa = (l.whatsapp || "").replace(/[^\d]/g, "");
                  return (
                    <tr
                      key={l.id}
                      className="border-t border-[#1f2a18] hover:bg-[#0F1A0C]/60 transition-colors"
                      data-testid={`lead-row-${l.id}`}
                    >
                      <td className="px-4 py-3 text-white/50 text-xs whitespace-nowrap">
                        {date ? date.toLocaleString("es-VE", {
                          day: "2-digit", month: "2-digit", year: "2-digit",
                          hour: "2-digit", minute: "2-digit",
                        }) : "—"}
                      </td>
                      <td className="px-4 py-3 font-body text-white">{l.name}</td>
                      <td className="px-4 py-3 font-body text-white/80">
                        <a href={`mailto:${l.email}`} className="hover:text-[#BEDD7D]">
                          {l.email}
                        </a>
                      </td>
                      <td className="px-4 py-3 font-body text-white/80 whitespace-nowrap">
                        <a
                          href={`https://wa.me/${wa}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:text-[#25D366]"
                        >
                          {l.whatsapp}
                        </a>
                      </td>
                      <td className="px-4 py-3 font-body text-white/70">{l.country || "—"}</td>
                      <td className="px-4 py-3 font-body text-white/60 capitalize">{l.experience || "—"}</td>
                      <td className="px-4 py-3">
                        <select
                          value={status}
                          onChange={(e) => updateStatus(l.id, e.target.value)}
                          data-testid={`status-${l.id}`}
                          className={`text-[10px] font-display font-bold uppercase tracking-[0.15em] rounded-md px-2 py-1.5 border outline-none cursor-pointer ${STATUS_COLORS[status] || STATUS_COLORS.nuevo}`}
                        >
                          {Object.keys(STATUS_LABELS).map((s) => (
                            <option key={s} value={s} className="bg-black">{STATUS_LABELS[s]}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => deleteLead(l.id)}
                          data-testid={`delete-${l.id}`}
                          className="text-white/40 hover:text-red-400 transition-colors text-[10px] font-display uppercase tracking-[0.15em]"
                        >
                          Eliminar
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="border-t border-[#1f2a18] px-4 py-3 text-[11px] text-white/40 font-body flex items-center justify-between">
            <span>Mostrando {filtered.length} de {leads.length}</span>
            <span className="font-display uppercase tracking-[0.2em]">Honor Trading · Admin v1</span>
          </div>
        </div>
      </main>
    </div>
  );
}

function StatCard({ label, value, highlight, status, onClick, active }) {
  const base =
    "rounded-xl border p-4 text-left transition-all min-h-[88px] flex flex-col justify-between";
  const cls = highlight
    ? "bg-[#BEDD7D]/10 border-[#BEDD7D]/40 text-white"
    : active
    ? `${STATUS_COLORS[status] || STATUS_COLORS.nuevo} ring-2 ring-current/40`
    : "bg-[#0A0F08] border-[#3E4D36]/50 hover:border-[#BEDD7D]/40 text-white";
  const Cmp = onClick ? "button" : "div";
  return (
    <Cmp onClick={onClick} className={`${base} ${cls}`}>
      <p className="text-[9px] font-display uppercase tracking-[0.22em] opacity-70">
        {label}
      </p>
      <p className="font-display font-bold text-3xl mt-2 leading-none">{value}</p>
    </Cmp>
  );
}
